import pick from 'lodash/pick';
import ObjectUtils from '@spothero/utils/object';
import {
    buildAirportSpotDetailsURL,
    buildAirportLogo,
    buildCheckoutURL,
    buildImageTemplate,
    buildUniqueRateAmenities,
    formatAmenitySlug,
    formatDateTime,
    formatDistance,
    formatAirportTransportation,
    formatHoursOfOperation,
    AmenityTypes,
} from '../utils';
import {
    OBJECT_AIRPORT_FACILITY_SUMMARY,
    OBJECT_AIRPORT_FACILITY,
    OBJECT_AIRPORT_RATE,
} from 'proptypes';

export function formatAirportRate({
    spotId,
    facilitySlug,
    quote: {order, advertisedPrice},
    additionalRateDetails,
    userRequestedStarts,
    userRequestedEnds,
    timezone,
    rebookId,
}) {
    // only keep properties that have corresponding rate type (transient, monthly, etc) prop types
    // and combine them with the additional rate details for that rate type as well as any additional
    // properties that rate type needs which are derived from other data not in the API
    const baseRate = order[0];
    const {rateId, starts: originalStarts, ends: originalEnds} = baseRate;
    const formattedRate = pick(
        {
            ...additionalRateDetails,
            ...baseRate,
            starts: formatDateTime(baseRate.starts, timezone),
            ends: formatDateTime(baseRate.ends, timezone),
            priceBreakdown: baseRate.items,
            amenities: additionalRateDetails.amenities.map(formatAmenitySlug),
            fullUrl: buildCheckoutURL({
                spotId,
                facilitySlug,
                rateId,
                rateType: 'airport',
                starts: originalStarts,
                ends: originalEnds,
                timezone,
                rebookId,
            }),
            spotDetailsUrl: buildAirportSpotDetailsURL({
                spotId,
                facilitySlug,
                starts: originalStarts,
                ends: originalEnds,
                timezone,
                rebookId,
            }),
        },
        Object.keys(OBJECT_AIRPORT_RATE)
    );

    return {
        ...formattedRate,
        advertisedPrice,
    };
}

/**
 * Formats data for a single airport facility
 *
 * @function formatAirportFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchAirportFacilityParking|Documentation}
 * @param {object} data - The untransformed data from the API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @param {object} formatOptions.userRequestedStarts - The start date/time that the request was made with
 * @param {object} formatOptions.userRequestedEnds - The end date/time that the request was made with
 * @param {string} formatOptions.rebookId - The ID of the original reservation that we are rebooking for
 * @param {boolean} formatOptions.isFacilitySummary - If true, will return the shortened version of the facility data (e.g. to display on spot cards)
 * @returns {object} The formatted airport facility data.
 */
export function formatAirportFacility(data, formatOptions = {}) {
    const {
        userRequestedStarts,
        userRequestedEnds,
        rebookId,
        isFacilitySummary,
    } = formatOptions;
    const dataFormat = isFacilitySummary
        ? OBJECT_AIRPORT_FACILITY_SUMMARY
        : OBJECT_AIRPORT_FACILITY;
    const facilityData = ObjectUtils.camelizeKeys(data);

    const {
        availability,
        distance,
        facility: {common, airport},
        rates: ratesResponse,
    } = facilityData;

    const timezone = common.addresses[0].timeZone;
    const {id: spotId, slug: facilitySlug, hoursOfOperation} = common;
    const {lowestDailyRate, tag, comparisons} = ratesResponse[0]?.airport || {};
    // only keep properties that have corresponding airport rate prop types
    const rates = ratesResponse.map(({quote, airport: additionalRateDetails}) =>
        formatAirportRate({
            spotId,
            facilitySlug,
            quote,
            additionalRateDetails,
            userRequestedStarts,
            userRequestedEnds,
            timezone,
            rebookId,
        })
    );

    const amenities = rates[0]?.amenities || [];
    const isCoveredParking = Boolean(
        amenities.find(item => item.type === AmenityTypes.COVERED_PARKING)
    );
    const isSelfParking = Boolean(
        amenities.find(item => item.type === AmenityTypes.SELF_PARK)
    );
    const isValetParking = Boolean(
        amenities.find(item => item.type === AmenityTypes.VALET)
    );

    // only keep properties that have corresponding airport facility prop types
    return pick(
        {
            isV2: true,
            ...common,
            ...airport,
            logo: buildAirportLogo(airport.logo, common.images),
            images: common.images.map(buildImageTemplate),
            spotId, // spotId is relied on in a lot of places over id
            availability,
            distance: formatDistance(distance),
            lowestDailyRate,
            cancellationAllowed:
                common.cancellation.allowedByCustomer ||
                common.cancellation.allowedBySpotheroCustomerService,
            tag: tag?.text,
            comparisons,
            transportation: formatAirportTransportation(airport.transportation),
            isCoveredParking,
            isSelfParking,
            isValetParking,
            rates,
            selectedRate: rates[0],
            allAmenities: buildUniqueRateAmenities(rates),
            hoursOfOperation: formatHoursOfOperation(hoursOfOperation),
            fullUrl: buildCheckoutURL({
                spotId,
                facilitySlug,
                rateType: 'airport',
                starts: userRequestedStarts,
                ends: userRequestedEnds,
                timezone,
                rebookId,
            }),
            spotDetailsUrl: buildAirportSpotDetailsURL({
                spotId,
                facilitySlug,
                starts: userRequestedStarts,
                ends: userRequestedEnds,
                timezone,
                rebookId,
            }),
        },
        Object.keys(dataFormat)
    );
}

/**
 * Formats data for a multiple airport facilities
 *
 * @function formatAirportSpotDetailsResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchAirportParking|Documentation}
 * @param {object} searchResults - Facility data returned from the search API for a multiple facilities
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {Array} The array of airport facilities, with data transformation applied.
 */
export function formatAirportSearchResponse(searchResults, formatOptions = {}) {
    const formattedResults = searchResults.map(result =>
        formatAirportFacility(result, {
            ...formatOptions,
            isFacilitySummary: true,
        })
    );

    return formattedResults;
}

/**
 * Formats data for a single airport facility
 *
 * @function formatAirportSpotDetailsResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchAirportFacilityParking|Documentation}
 * @param {object} facilityData - All the data returned from the search API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {object} The airport facility, with data transformation applied.
 */
export function formatAirportSpotDetailsResponse(facilityData, formatOptions) {
    return formatAirportFacility(facilityData, {
        ...formatOptions,
        isFacilitySummary: false,
    });
}
